import not from 'lodash/negate';
import last from 'lodash/last';

import conditionOperators, { and, or } from './condition-operators';

const MIN_LOGIC_LENGTH = 3;

const logic = (...operators) => {
    const areSameConditionalOperators = getAreSameConditionalOperators(operators);
    const isInRightOrder = getIsInRightOrder(operators);

    if (!isInRightOrder) {
        throw new Error('operators are not in the correct order');
    }

    if (!areSameConditionalOperators) {
        const isANDOperator = op => Array.isArray(op);

        operators = collapseANDOperators(operators)
            .map(operator => isANDOperator(operator) ? getLogicObject(operator) : operator);
    }

    return getLogicObject(operators);
};

export const logicOr = (...operators) => getLogicObject(operators, or);
export const logicAnd = (...operators) => getLogicObject(operators, and);

const getLogicObject = (operators, condition = getConditionOperator(operators)) => {
    const source = getSourceOperators(operators);

    return source.length > 1
        ? ({
            type: 'LOGIC',
            value: condition,
            source
        })
        : source[0];
};

export const getIsInRightOrder = (operators) => (
    operators.length >= MIN_LOGIC_LENGTH // it has at least MIN_LOGIC_LENGTH items
    && operators.length % 2 !== 0 // length should be an even number
    // every even indexed value is a condition operator. every odd — non-condition operator.
    && operators
        .every((op, index) => isOdd(index) ? !getIsConditionOperator(op) : getIsConditionOperator(op))
);

export const collapseANDOperators = ([...operators]) => {
    const ANDIndexes = operators
        .map((operator, opIndex) => ({ operator, opIndex })) // get an operator value and it's index
        .filter(({ operator }) => operator === and) // keep only AND conditions
        .map(({ opIndex }) => [opIndex - 1 , opIndex + 1])// get glued indexes [leftValueIndex, rightValueIndex]
        .reduce((acc, [left, right]) => { // merge connected indexes. [0,2],[2,4] => [0,4]
            const prev = last(acc) || [];
            const rightIndex = 1;

            if (prev[rightIndex] === left) {
                prev[rightIndex] = right;
            } else {
                acc.push([left, right]);
            }

            return acc;
        }, []);

    ANDIndexes.forEach(([from, to]) => {
        const separated = operators.splice(from, to - from + 1);
        operators.splice(from, 0, separated, ...Array(separated.length - 1));
    });

    return operators.filter(Boolean);
};

export const getAreSameConditionalOperators = (operators) => {
    const conditionOperatorsArr = getConditionOperators(operators);

    return conditionOperatorsArr.every(op => op === conditionOperatorsArr[0]);
};

const isOdd = number => number % 2 === 0;

const getIsConditionOperator = op => conditionOperators.includes(op);

const getConditionOperators = (operators) => operators.filter(getIsConditionOperator);
const getConditionOperator = operators => operators.find((op) => conditionOperators.includes(op));

const getSourceOperators = (operators) => operators.filter(not(getIsConditionOperator));

export default logic;
